import type { InspectionFormState, InspectionFormAction } from "../useInspectionForm";

interface Props {
  state: InspectionFormState;
  dispatch: React.Dispatch<InspectionFormAction>;
  treeData: { ctaCode: string; species: string; scientificName: string; address: string } | null | undefined;
}

const labelCls = "text-xs font-medium text-foreground-500 mb-1 block";
const selectCls = "w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors appearance-none cursor-pointer";

const BOTANICAL_STATUSES = [
  { value: "CONFIRMED", label: "Confirmada" },
  { value: "PROBABLE", label: "Provável" },
  { value: "UNIDENTIFIED", label: "Não identificada" },
];

export default function StepIdentificacao({ state, dispatch, treeData }: Props) {
  return (
    <div className="space-y-5">
      {/* Tree identity (read-only from cadastro) */}
      <div className="bg-background-50 rounded-xl p-4 border border-background-200/50">
        <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">
          Dados da Árvore (cadastro)
        </h4>
        {treeData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <InfoRow label="Código CTA" value={treeData.ctaCode} mono />
            <InfoRow label="Espécie" value={treeData.species} />
            <InfoRow label="Nome científico" value={treeData.scientificName} italic />
            <InfoRow label="Endereço" value={treeData.address} />
          </div>
        ) : (
          <p className="text-sm text-foreground-400">Carregando dados da árvore...</p>
        )}
      </div>

      {/* Location confirmation */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={state.locationConfirmed}
            onChange={(e) =>
              dispatch({ type: "SET_FIELD", field: "locationConfirmed", value: e.target.checked })
            }
            className="accent-primary-500 mt-0.5"
          />
          <div>
            <p className="text-sm font-medium text-foreground-800">Localização confirmada em campo</p>
            <p className="text-xs text-foreground-500 mt-0.5">
              Confirmo que a árvore foi localizada e corresponde ao cadastro acima.
            </p>
          </div>
        </label>
      </div>

      {/* Botanical identification */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-foreground-800 flex items-center gap-2">
          <i className="ri-leaf-line text-primary-500"></i>
          Identificação Botânica
        </h4>
        <div>
          <label className={labelCls}>Status da identificação</label>
          <select
            value={state.botanicalIdentificationStatus}
            onChange={(e) =>
              dispatch({ type: "SET_FIELD", field: "botanicalIdentificationStatus", value: e.target.value })
            }
            className={selectCls}
          >
            <option value="">Selecione...</option>
            {BOTANICAL_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Observações botânicas</label>
          <textarea
            rows={3}
            value={state.botanicalNotes}
            onChange={(e) =>
              dispatch({ type: "SET_FIELD", field: "botanicalNotes", value: e.target.value })
            }
            placeholder="Observações sobre a identificação botânica..."
            className="w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
          />
        </div>
      </div>

      {/* Inspection date */}
      <div className="bg-white rounded-xl border border-background-200/70 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Data da inspeção</label>
            <input
              type="date"
              value={state.inspectionDate}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "inspectionDate", value: e.target.value })
              }
              className="w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
            />
          </div>
          <div>
            <label className={labelCls}>Observações gerais</label>
            <input
              type="text"
              value={state.notes}
              onChange={(e) =>
                dispatch({ type: "SET_FIELD", field: "notes", value: e.target.value })
              }
              placeholder="Observações..."
              className="w-full bg-white border border-background-200/70 rounded-lg px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value, mono, italic }: { label: string; value: string; mono?: boolean; italic?: boolean }) {
  return (
    <div>
      <span className="text-xs text-foreground-400">{label}:</span>
      <span className={`ml-1 text-sm font-medium text-foreground-700 ${mono ? "font-mono text-accent-600" : ""} ${italic ? "italic" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}
